import { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import DynamicTable from "../DynamicTable/DynamicTable";
import {
  excelFormateDataForStageSearch,
  camelCaseToTitleCase,
} from "../../helpers/utils";
import TableTopToolBar from "../../Pages/Reports/TopToolBarStateSearch";
import { useLocation } from "react-router-dom";
import moment from "moment";
import Export from "../Export/ExportToXLSX";
import SnackBars from "../snackbars/SnackBars";
import { size } from "lodash";

const StageSearchReportTable = ({
  fetchData,
  exportFileNamePrefix = "Site Report",
}) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const route = useLocation();
  const renderExportToolbar = route.pathname.includes("/reports/");

  const [sitesReport, setSitesReport] = useState([]);
  const [totalSitesReport, setTotalSitesReport] = useState(0);
  const [rowSelection, setRowSelection] = useState({});
  const [columns, setColumns] = useState({});
  const [filteredData, setFilteredData] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [isRefetching, setIsRefetching] = useState(false);
  const [taskDetails, setTaskDetails] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [allSiteExportData, setAllSiteExportData] = useState([]);
  const [allSitePageData, setAllSitePageData] = useState([]);
  const [selectedState, setSelectedState] = useState("dummy");

  useEffect(() => {
    const getData = setTimeout(() => {
      getReport();
    }, 500);
    return () => clearTimeout(getData);
  }, [pagination.pageIndex, pagination.pageSize, columnFilters]);

  const getReport = async () => {
    try {
      setIsRefetching(true);
      const allSiteReport = await fetchData(
        pagination.pageIndex + 1,
        pagination.pageSize,
        columnFilters
      );
      setSitesReport(allSiteReport?.data);
      setTotalSitesReport(allSiteReport?.totalCount);
      if (allSiteReport.totalCount > 0) setColumns(allSiteReport?.data[0]);
      setAllSiteExportData(allSiteReport?.allData);
      setAllSitePageData(allSiteReport?.data);
      setIsRefetching(false);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setSitesReport([]);
        setTotalSitesReport(0);
        setColumns({});
        setSnackbarMessage("No Data Found.");
        setSnackbarSeverity("warning");
      } else {
        setSnackbarMessage("Internal Server Error !");
        setSnackbarSeverity("error");
      }
      setSnackbarOpen(true);
    }
  };

  const handleColumnFiltersChange = (newFilters) => {
    const filters =
      typeof newFilters === "function" ? newFilters(columnFilters) : newFilters;

    const colFilters = filters?.map((fil) => {
      if (fil.id === "stage") {
        // Ensure dropdown value is used as-is
        return {
          id: fil.id,
          value: fil.value,
        };
      } else {
        return { ...fil };
      }
    });

    setColumnFilters(colFilters);
  };

  const memoisedTableColumns = useMemo(() => {
    const dynamicColumns = Object.keys(columns)
      .filter(
        (key) =>
          key !== "id" &&
          key !== "createdAt" &&
          key !== "updatedAt" &&
          key !== "updatedBy" &&
          key !== "activeFlag" &&
          key !== "SiteID" &&
          key !== "ActivityOrder"
      )
      .map((key) => {
        const header = camelCaseToTitleCase(key);
        const width = Math.min(Math.max(header.length * 12, 100), 250);

        const columnDef = {
          accessorKey: key,
          header,
          size: width,
        };

        if (key === "omc") {
          columnDef.filterFn = "equals";
          columnDef.filterVariant = "select";
          columnDef.filterSelectOptions = ["BPCL", "IOCL", "HPCL"];
        }

        if (key === "ActivityName") {
          columnDef.filterFn = "equals";
          columnDef.filterVariant = "select";
          columnDef.filterSelectOptions = [
            "SIL Management Approval",
            "OMC Application",
            "OMC NOC Receipt",
            "Application to corporation",
            "Corporation NOC Receipt",
            "Release Foundation WO",
            "Release Structure WO",
            "Release LED WO",
            "Lease Agreement",
            "Installation",
            "Electrification",
            "GO Live",
          ];
        }

        if (key === "siteProcess") {
          columnDef.filterFn = "equals";
          columnDef.filterVariant = "select";
          columnDef.filterSelectOptions = ["Continue", "Surrender", "Hold"];
        }

        if (key === "cityPriority") {
          columnDef.filterFn = "equals";
          columnDef.filterVariant = "select";
          columnDef.filterSelectOptions = ["A", "B", "C"];
        }

        return columnDef;
      });
    return [...dynamicColumns];
  }, [columns, taskDetails]);

  const exportSelectedData = (data) => {
    const currentDate = moment().format("YYYY-MM-DD");
    const updatedData = excelFormateDataForStageSearch(data);
    Export(updatedData, `${exportFileNamePrefix} - ${currentDate}.xlsx`);
  };

  const exportPageData = () => {
    const currentDate = moment().format("YYYY-MM-DD");
    const updatedData = excelFormateDataForStageSearch(allSitePageData);
    Export(updatedData, `${exportFileNamePrefix} - ${currentDate}.xlsx`);
  };

  const exportAllData = async () => {
    const updatedData = excelFormateDataForStageSearch(allSiteExportData);
    const currentDate = moment().format("YYYY-MM-DD");
    Export(updatedData, `${exportFileNamePrefix} - ${currentDate}.xlsx`);
  };

  const TopToolBarWithButtons = ({ table }) => {
    return (
      <TableTopToolBar
        table={table}
        exportPageData={exportPageData}
        exportSelectedData={exportSelectedData}
        exportAllData={exportAllData}
        // setSitesReport={setSitesReport}
      />
    );
  };

  return (
    <Box width={"100%"}>
      <Box>
        <DynamicTable
          data={sitesReport}
          columns={memoisedTableColumns}
          totalSitesCount={totalSitesReport}
          setPagination={setPagination}
          pagination={pagination}
          isEnablePagination={true}
          isFilterEnable={true}
          isSearchBarEnable={true}
          isEnableSorting={true}
          isHeaderBackgroundColor={false}
          showTableTopToolbar={true}
          enableColumnActions={true}
          rowSelection={rowSelection}
          updateRowSelection={setRowSelection}
          TopToolBar={TopToolBarWithButtons}
          enableRowSelection={true}
          updateFilteredData={setFilteredData}
          columnFilters={columnFilters}
          setColumnFilters={handleColumnFiltersChange}
          isRefetching={isRefetching}
          isServerSideFiltering={true}
        />
      </Box>
      <SnackBars
        open={snackbarOpen}
        autoHideDuration={6000}
        handleClose={() => setSnackbarOpen(false)}
        title={snackbarMessage}
        type={snackbarSeverity}
      />
    </Box>
  );
};

export default StageSearchReportTable;
